/**
 * utils/smartTableConverter.ts
 *
 * Converts SmartTableRow[] into NeedItem[].
 *
 * Created April 2026: supports the smart-table form creation workflow.
 * Updated April 2026: resolves conditionRaw strings to FieldCondition[] after
 *   all needs are created, supporting format "Si [Label] = Val1, Val2".
 * Updated April 2026: supports the name (Intitulé) + label (Question) split;
 *   NeedItem.label = row.label (question) || row.name (fallback), mirroring
 *   the catalog's question-first paradigm.
 * Updated April 2026: fixed effectiveLabel computation to trim each field before
 *   the fallback (row.label.trim() || row.name.trim()) so a whitespace-only label
 *   correctly falls back to name, consistent with SmartTable's filledRows filter.
 * Updated April 2026: labelToId now indexes both row.name and row.label for
 *   condition resolution; supports compound conditions using "ET Si" (AND) and
 *   "OU Si" (OR); isRequired mapped from row.isRequired instead of hardcoded true.
 * Updated April 2026: NeedItem.name now set from row.name to preserve Intitulé
 *   for display as an admin chip in NeedItem.tsx.
 * Updated April 2026: compound-condition parsing is now atomic - if any fragment
 *   fails to resolve the entire condition is discarded; added failedConditionsCount
 *   to ConversionResult so callers can surface unresolved conditions to the user.
 * Updated April 2026: ambiguous labelToId key collision detection - when two
 *   different fields map to the same lowercased key the key is recorded as ambiguous
 *   and removed so condition resolution stays unresolved rather than silently binding
 *   to the last writer.
 * Updated April 2026: added tooManyConditionsCount to ConversionResult; compound
 *   conditions with more than 3 fragments are rejected at parse time.
 * Updated April 2026: convertRowsToNeeds accepts an optional existingNeeds param so
 *   conditions imported into a non-empty form can reference fields from prev state.
 * Updated June 2026: removed catalog matching (catalog system removed). Labels come
 *   directly from the CSV row. Document rows use row.name (Intitulé) as label.
 * Updated June 2026: when both row.name (Intitulé) and row.label (Question) are
 *   present, short_label is set to row.name so the admin sees the short title in
 *   the client detail view while the client sees the full question.
 */

import type { NeedItem, FieldCondition } from '../types/needs';
import type { SmartTableRow } from './smartTableParser';

export interface ConversionResult {
  needs: NeedItem[];
  skippedCount: number;
  failedConditionsCount: number;
  tooManyConditionsCount: number;
}

function parseSingleConditionFragment(
  fragment: string,
  labelToId: Map<string, string>,
): FieldCondition | null {
  const condStr = fragment.replace(/^si\s+/i, '').replace(/^if\s+/i, '');
  const eqIdx = condStr.indexOf('=');
  if (eqIdx === -1) return null;

  const sourceLabel = condStr.substring(0, eqIdx).trim().toLowerCase();
  const valuesStr = condStr.substring(eqIdx + 1).trim();
  const values = valuesStr.split(',').map((v) => v.trim()).filter(Boolean);

  const sourceId = labelToId.get(sourceLabel);
  if (!sourceId || values.length === 0) return null;

  return { source_field_id: sourceId, values };
}

function parseConditionRaw(
  raw: string,
  labelToId: Map<string, string>,
): { conditions: NeedItem['conditions']; operator: 'or' | 'and' | undefined; tooManyFragments: boolean } {
  const andMatch = / ET Si /i.test(raw);
  const orMatch = / OU Si /i.test(raw);

  if (andMatch || orMatch) {
    const delimiter = andMatch ? / ET Si /i : / OU Si /i;
    const operator: 'or' | 'and' = andMatch ? 'and' : 'or';
    const fragments = raw.split(delimiter);

    if (fragments.length > 3) {
      return { conditions: null, operator: undefined, tooManyFragments: true };
    }

    const conditions: FieldCondition[] = [];
    for (const fragment of fragments) {
      const cond = parseSingleConditionFragment(fragment.trim(), labelToId);
      if (cond) conditions.push(cond);
    }

    if (conditions.length !== fragments.length || conditions.length === 0) {
      return { conditions: null, operator: undefined, tooManyFragments: false };
    }
    return { conditions, operator, tooManyFragments: false };
  }

  const single = parseSingleConditionFragment(raw, labelToId);
  return { conditions: single ? [single] : null, operator: undefined, tooManyFragments: false };
}

function buildLabelToId(
  rows: SmartTableRow[],
  needs: NeedItem[],
  existingNeeds?: NeedItem[],
): Map<string, string> {
  const labelToId = new Map<string, string>();
  const ambiguousKeys = new Set<string>();

  const registerKey = (key: string, id: string) => {
    if (ambiguousKeys.has(key)) return;
    if (labelToId.has(key) && labelToId.get(key) !== id) {
      ambiguousKeys.add(key);
      labelToId.delete(key);
    } else {
      labelToId.set(key, id);
    }
  };

  if (existingNeeds) {
    for (const existing of existingNeeds) {
      if (existing.type !== 'single_choice' && existing.type !== 'multiple_choice') continue;
      registerKey(existing.label.trim().toLowerCase(), existing.id);
      if (existing.name?.trim()) registerKey(existing.name.trim().toLowerCase(), existing.id);
    }
  }

  let i = 0;
  for (const row of rows) {
    const effectiveLabel = row.label.trim() || row.name.trim();
    if (!effectiveLabel) continue;
    const need = needs[i++];
    if (need.type !== 'single_choice' && need.type !== 'multiple_choice') continue;
    if (row.name.trim()) registerKey(row.name.trim().toLowerCase(), need.id);
    if (row.label.trim()) registerKey(row.label.trim().toLowerCase(), need.id);
    registerKey(need.label.trim().toLowerCase(), need.id);
  }

  return labelToId;
}

export function convertRowsToNeeds(
  rows: SmartTableRow[],
  existingNeeds?: NeedItem[],
): ConversionResult {
  const needs: NeedItem[] = [];
  let skippedCount = 0;
  let failedConditionsCount = 0;
  let tooManyConditionsCount = 0;

  for (const row of rows) {
    const nameStr = row.name.trim();
    const labelStr = row.label.trim();

    // For documents: prefer row.name (Intitulé) as the effective label because
    // that is the short administrative label, not the client-facing question.
    // For all other types: prefer question (row.label) then fall back to name.
    const effectiveLabel =
      row.type === 'document'
        ? nameStr || labelStr
        : labelStr || nameStr;

    if (!effectiveLabel) {
      skippedCount++;
      continue;
    }

    const isChoice = row.type === 'single_choice' || row.type === 'multiple_choice';

    // When both Intitulé (name) and Question (label) are provided, use Question
    // as the client-facing label and Intitulé as the short admin label.
    // When only Intitulé is provided, it serves as both.
    const hasDistinctQuestion = nameStr && labelStr && nameStr !== labelStr;

    const need: NeedItem = {
      id: crypto.randomUUID(),
      type: row.type,
      ...(nameStr ? { name: nameStr } : {}),
      label: effectiveLabel,
      short_label: hasDistinctQuestion ? nameStr : null,
      description: row.description.trim() || null,
      is_required: row.isRequired,
      position: needs.length,
      conditions: null,
      section: null,
      ...(isChoice && { options: row.options.length > 0 ? row.options : ['Option 1', 'Option 2'] }),
    };

    needs.push(need);
  }

  const labelToId = buildLabelToId(rows, needs, existingNeeds);

  let needIdx = 0;
  for (const row of rows) {
    const nameStr = row.name.trim();
    const labelStr = row.label.trim();
    const effectiveLabel =
      row.type === 'document' ? nameStr || labelStr : labelStr || nameStr;
    if (!effectiveLabel) continue;
    const need = needs[needIdx++];
    const raw = row.conditionRaw?.trim();
    if (!raw) continue;

    const { conditions, operator, tooManyFragments } = parseConditionRaw(raw, labelToId);
    need.conditions = conditions;
    if (conditions === null) {
      if (tooManyFragments) {
        tooManyConditionsCount++;
      } else {
        failedConditionsCount++;
      }
    }
    if (operator !== undefined) need.condition_operator = operator;
  }

  return { needs, skippedCount, failedConditionsCount, tooManyConditionsCount };
}
